import {
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  Tooltip,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import PageTitle from "../atoms/PageTitle";
import Card from "components/Cards/Card";
import AddAchievementSection from "./AddAchievementSection";
import { useTranslation } from "react-i18next";
import CreateFormSection from "../atoms/CreateFormSection";
import { isTrue } from "helpers/utils/validators";
import { createEvent } from "helpers/utils/utilities";
import useMaster from "store/master/service-hook";
import Input from "components/Input";
import { Check, Delete, Search } from "@mui/icons-material";
import ListingContainer from "./ListingContainer";
import SecureImage from "../atoms/SecureImage";
import trophy from "assets/png/goldTrophy.png";
import Button from "components/Button";
import MasterSelect from "./MasterSelect";
import { COLORS } from "helpers/utils/constants";
import useOrganization from "store/organization/service-hook";

const AchievementStep = ({
  data,
  errors,
  onChange,
  component,
  hidePrebuilt,
  showAchievementConditions,
}) => {
  const [plan, setPlan] = useState(false);
  const { t } = useTranslation("common");
  const { planDetails } = useOrganization();

  useEffect(() => {
    fetchPlanDetails();
  }, []);

  const fetchPlanDetails = async () => {
    const res = await planDetails();
    if (res) {
      setPlan(res);
    }
  };

  return (
    <Grid
      className={plan?.plan === "seed_plan_yearly" ? "opacity-50" : ""}
      container
      px={{ xs: 0, md: 4 }}
    >
      <PageTitle>{t("Achievements")}</PageTitle>
      <Card classes={"w-full mt-4"}>
        <div>
          <h3>{t("Enable Achievement")}</h3>
          <FormControlLabel
            control={
              <Switch
                name="is_achievement_enabled"
                checked={isTrue(data?.is_achievement_enabled)}
                onChange={(e) =>
                  onChange(
                    createEvent("is_achievement_enabled", e.target.checked)
                  )
                }
                disabled={plan?.plan === "seed_plan_yearly"}
              />
            }
            label={
              <span className="fs-12 c-title">
                {t("Enable users to get trophies")}
              </span>
            }
          />
        </div>
        {data?.is_achievement_enabled && showAchievementConditions && (
          <MasterSelect
            onChange={onChange}
            value={data["achievement_conditions"]}
            name="achievement_conditions"
            classes="w-full mt-3"
            title="Conditions to award"
            width="99%"
            t={t}
            multi
            required
          />
        )}
      </Card>
      {isTrue(data?.is_achievement_enabled) && (
        <>
          {!hidePrebuilt && (
            <CreateFormSection classes="w-full mt-2">
              <PreBuiltAchievements
                component={component}
                data={data}
                onChange={onChange}
              />
            </CreateFormSection>
          )}
          <CreateFormSection
            sx={{
              pointerEvents: Boolean(data?.pre_built_achievement_id) && "none",
            }}
            classes={`w-full mt-2 ${
              Boolean(data?.pre_built_achievement_id)
                ? "opacity-50"
                : "opacity-100"
            }`}
          >
            <AddAchievementSection
              data={data}
              errors={errors}
              onChange={onChange}
              showAchievementConditions={showAchievementConditions}
              showAchivementEnabled
              disabled={plan?.plan === "seed_plan_yearly"}
              title={hidePrebuilt ? false : t("Or Create Manually")}
            />
          </CreateFormSection>
          <PreviewAchievement
            data={data}
            t={t}
            onChange={onChange}
            xl={3}
            lg={4}
            style={{
              pointerEvents: Boolean(data?.pre_built_achievement_id) && "none",
              opacity: Boolean(data?.pre_built_achievement_id) ? 0.5 : 1,
            }}
            isPariticipation={
              window?.location?.pathname?.includes("/edit") ||
              window?.location?.pathname?.includes("/create")
            }
          />
        </>
      )}
    </Grid>
  );
};

export default AchievementStep;

export const PreviewAchievement = ({
  data,
  t = (d) => d,
  onChange = Function.prototype,
  xl = 4,
  lg = 6,
  style = {},
  isPariticipation,
}) => {
  const clearUploadAchievement = () => {
    ["achievement_image", "achievement_name", "achievement_points"].forEach(
      (d) => onChange(createEvent(d, ""))
    );
  };

  return (
    <div className="w-full" style={style}>
      <h3 className="mt-4">{t("Preview Uploaded Achievement")}</h3>
      {Boolean(data?.achievement_name) ? (
        <Grid
          container
          xs={12}
          md={6}
          lg={lg}
          xl={xl}
          columnSpacing={1}
          alignItems="center"
          mt={2}
        >
          <Grid item xs={4}>
            <SecureImage
              src={data?.achievement_image}
              placeholder={trophy}
              classes="h-80 w-80 rounded-100 object-cover border"
            />
          </Grid>
          <Grid item xs={7}>
            <p className="fs-15 fw-500 text-truncate">
              {data?.achievement_name || "N/A"}
            </p>
            <p className="fs-13 c-title my-1">
              {data?.achievement_points ?? "0"} {t("Prepr Points")}
            </p>
            <Chip
              className="fs-13 h-25 fs-12--mob capitalize rounded-5 c-title border"
              label={isPariticipation ? t("Participation") : t("Appreciation")}
            />
          </Grid>
          <Grid item xs={1}>
            <IconButton onClick={clearUploadAchievement}>
              <Delete color="error" fontSize="small" />
            </IconButton>
          </Grid>
        </Grid>
      ) : (
        <p className="w-full text-center my-10 c-gray fs-13">
          {t("Upload achievement details to preview!")}
        </p>
      )}
    </div>
  );
};

export const PreBuiltAchievements = ({
  component,
  data = {},
  onChange = Function.prototype,
  classes = "",
  isUserAwardPopup,
  receiverId,
  isWinner,
}) => {
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState({ value: "", current: "" });
  const { fetchPreBuiltAchievements } = useMaster();
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const { t } = useTranslation("common");

  const key = useMemo(
    () =>
      isWinner ? "pre_built_winner_achievement_id" : "pre_built_achievement_id",
    [isWinner]
  );

  useEffect(() => {
    fetchAchievements();
  }, [page, search.value]);

  const fetchAchievements = async () => {
    setLoading(true);
    const payload = {
      component_type: component,
      page,
      search: search.value,
    };
    if (receiverId) payload["receiver_id"] = receiverId;
    const res = await fetchPreBuiltAchievements(payload);
    setAchievements(res?.list || []);
    if (res?.total_pages < res?.current_page) setPage(res?.total_pages);
    setPages(res?.total_pages);
    setLoading(false);
  };

  const handleSelect = (id, isAwarded) => {
    if (isUserAwardPopup && isAwarded) return;
    let value = null;
    if (isWinner) {
      if (!data[key] || !data?.[key]?.length) {
        value = [id];
      } else if (data?.[key]?.includes(id)) {
        value = data[key]?.filter((d) => d !== id);
      } else {
        value = [...(data?.[key] || []), id];
      }
    } else {
      if (data?.[key] !== id) {
        value = id;
      }
    }
    onChange(createEvent(key, value));
  };

  const isSelected = (id) => {
    return isWinner ? data[key]?.includes(id) : data[key] === id;
  };

  return (
    <div className={`w-full ${classes}`}>
      {!isUserAwardPopup && (
        <>
          <h3>{t("Select from Pre-built Achievements")}</h3>
          <p>
            {t(
              "We have created a list of pre-built achievements to help you quickly set up ${component} achievements.",
              {
                component: t(component),
              }
            )}
          </p>
        </>
      )}
      <Input
        icon={<Search />}
        width="100%"
        height="50px"
        classes="w-full mt-2"
        iconLeft
        placeholder={t("Search for a Pre-built Achievement")}
        disabled={loading}
        value={search.current}
        onChange={(e) =>
          setSearch((prev) => ({
            value: !e.target.value ? "" : prev.value,
            current: e.target.value,
          }))
        }
        onKeyDown={(e) =>
          e.key === "Enter" &&
          setSearch((prev) => ({ ...prev, value: prev.current }))
        }
      />
      <ListingContainer
        loading={loading}
        spinLoader
        columnSpacing={2}
        rowSpacing={2}
        classes="mt-3"
        page={page}
        count={pages}
        setPage={setPage}
        emptyText={t("No data found!")}
        isEmpty={!achievements?.length}
        emptySmall
      >
        {achievements?.map((ach) => (
          <Grid key={ach?.achievement_id} item xs={12} sm={6} md={4} lg={2.4}>
            <Card
              noShadow
              classes="border flex flex-column items-center c-title overflowX-hidden"
            >
              <SecureImage
                src={ach?.achievement_image}
                placeholder={trophy}
                classes="h-100 maxw-120"
              />
              <Tooltip title={ach?.title} placement="top">
                <p className="fs-15 fw-600 cursor-pointer text-truncate-2 tracking-tight h-40 text-center w-full">
                  {ach?.title}
                </p>
              </Tooltip>
              <p className="fs-13 mt-3 text-center tracking-tight text-nowrap">
                {ach?.points || "0"} {t("Prepr Points")}
              </p>
              <Chip
                className="fw-500 whitespace-nowrap fs-12--mob mt-2 capitalize rounded-10"
                sx={{
                  width: "fit-content",
                  height: 25,
                  overflow: "visible",
                  textOverflow: "unset",
                }}
                label={
                  isWinner
                    ? t("Winner")
                    : isUserAwardPopup
                    ? t("Appreciation")
                    : t("Participation")
                }
              />
              <Button
                tooltip={
                  ach?.is_awarded
                    ? t(
                        "You have already awarded this Achievement to the selected user."
                      )
                    : null
                }
                color={
                  ach?.is_awarded
                    ? "gray"
                    : isSelected(ach?.achievement_id)
                    ? "green"
                    : "blue"
                }
                bordered={isSelected(ach?.achievement_id)}
                classes="mt-4 h-35"
                onClick={() =>
                  handleSelect(ach?.achievement_id, ach?.is_awarded)
                }
                tooltipProps={{
                  componentsProps: {
                    tooltip: {
                      sx: {
                        fontSize: 12,
                        textAlign: "center",
                        bgcolor: COLORS.BLUE_OPAQUE,
                        "& .MuiTooltip-arrow": {
                          color: COLORS.BLUE_OPAQUE,
                        },
                        color: "#404252",
                      },
                    },
                  },
                }}
              >
                {ach?.is_awarded ? (
                  t("Awarded")
                ) : isSelected(ach?.achievement_id) ? (
                  <>
                    <Check fontSize="small" /> {t("Selected")}
                  </>
                ) : (
                  t("Select")
                )}
              </Button>
            </Card>
          </Grid>
        ))}
      </ListingContainer>
    </div>
  );
};
